import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import toast from 'react-hot-toast';
import posthog from 'posthog-js';
import LightningCanvas from '../components/LightningCanvas';
import './SimpleLandingPage.css';

const EXAMPLE_CHIPS = [
  'AI productivity app for developers',
  'Sustainable fashion marketplace',
  'Team collaboration tool',
  'Mental wellness platform',
];

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly',     label: 'Friendly' },
  { value: 'modern',       label: 'Modern' },
  { value: 'classic',      label: 'Classic' },
];

const NAMING_STRATEGY_OPTIONS = [
  { value: 'portmanteau', label: 'Portmanteau' },
  { value: 'compound',    label: 'Compound' },
  { value: 'suggestive',  label: 'Suggestive' },
  { value: 'invented',    label: 'Invented' },
  { value: 'descriptive', label: 'Descriptive' },
];

const TLD_OPTIONS = ['.com', '.io', '.ai', '.co', '.dev'];

const FEATURE_PILLS = [
  { color: '#10b981', label: 'Live domain availability' },
  { color: '#60a5fa', label: 'USPTO trademark search' },
  { color: '#f59e0b', label: 'AI brand scoring' },
];

function formatCount(n: number): string {
  const k = n / 1000;
  return k.toFixed(1).replace(/\.0$/, '') + 'k';
}

export default function SimpleLandingPage() {
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();

  const [keywords, setKeywords] = useState('');
  const [tone, setTone] = useState('professional');
  const [namingStrategy, setNamingStrategy] = useState('portmanteau');
  const [tld, setTld] = useState('.com');
  const [configOpen, setConfigOpen] = useState(false);
  const [counterDisplay, setCounterDisplay] = useState('—');

  useEffect(() => {
    let count = 11847 + Math.floor(Math.random() * 600);
    setCounterDisplay(formatCount(count));

    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const r = Math.random();
      count += r < 0.65 ? 1 : r < 0.91 ? 2 : 3;
      setCounterDisplay(formatCount(count));
      timer = setTimeout(tick, 900 + Math.random() * 3800);
    };
    timer = setTimeout(tick, 1500 + Math.random() * 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleGenerate = () => {
    if (!keywords.trim()) {
      toast.error('Please describe your brand or product');
      return;
    }
    posthog.capture('landing_generate_clicked', { keywords, tone, namingStrategy, tld });
    navigate('/app', { state: { keywords, tone, namingStrategy, tld } });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#050810',
      color: '#f1f5f9',
      fontFamily: "'Inter', system-ui, sans-serif",
      overflowX: 'hidden',
      position: 'relative',
    }}>
      {/* Mesh gradient */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: [
          'radial-gradient(ellipse 80% 60% at 18% -8%, rgba(99,102,241,0.16) 0%, transparent 60%)',
          'radial-gradient(ellipse 60% 50% at 82% 8%, rgba(139,92,246,0.11) 0%, transparent 55%)',
          'radial-gradient(ellipse 50% 40% at 50% 100%, rgba(16,185,129,0.05) 0%, transparent 50%)',
        ].join(', '),
      }} />

      {/* 48px grid with radial fade */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        backgroundImage: [
          'linear-gradient(rgba(255,255,255,0.022) 1px, transparent 1px)',
          'linear-gradient(90deg, rgba(255,255,255,0.022) 1px, transparent 1px)',
        ].join(', '),
        backgroundSize: '48px 48px',
        maskImage: 'radial-gradient(ellipse 90% 70% at 50% 0%, black 20%, transparent 85%)',
        WebkitMaskImage: 'radial-gradient(ellipse 90% 70% at 50% 0%, black 20%, transparent 85%)',
      }} />

      {/* Lightning animation (desktop only — disabled <768px inside component) */}
      <LightningCanvas />

      {/* ── Nav ── */}
      <nav style={{
        position: 'relative', zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 40px', height: 64,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(16px)',
        background: 'rgba(5,8,16,0.65)',
      }}>
        <a href="/" style={{
          display: 'flex', alignItems: 'center', gap: 10,
          fontWeight: 800, fontSize: 18, letterSpacing: '-0.5px',
          color: '#f1f5f9', textDecoration: 'none',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 17, lineHeight: '1',
          }}>⚡</div>
          Blitzname
        </a>

        <ul style={{ display: 'flex', alignItems: 'center', gap: 4, listStyle: 'none', margin: 0, padding: 0 }}>
          <li><a href="/blog" className="landing-nav-link">Features</a></li>
          <li><a href="/pricing" className="landing-nav-link">Pricing</a></li>
          <li><a href="/blog" className="landing-nav-link">Blog</a></li>
        </ul>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="landing-btn-ghost" onClick={() => loginWithRedirect()}>
            Sign in
          </button>
          <button className="landing-btn-nav-cta" onClick={() => navigate('/app')}>
            Get started free
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{
        position: 'relative', zIndex: 5,
        padding: '88px 40px 48px',
        textAlign: 'center',
        maxWidth: 760, margin: '0 auto',
      }}>
        <h1 style={{
          fontSize: 'clamp(40px, 5.5vw, 60px)',
          fontWeight: 900, lineHeight: 1.07, letterSpacing: '-2.5px',
          marginBottom: 22, marginTop: 0,
        }}>
          The domain name<br />
          <span style={{
            background: 'linear-gradient(135deg, #e0e7ff 0%, #6366f1 40%, #8b5cf6 72%, #c4b5fd 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            your brand deserves.
          </span>
        </h1>

        <p style={{
          fontSize: 17, color: '#94a3b8', lineHeight: 1.65,
          maxWidth: 500, margin: '0 auto 24px', fontWeight: 400,
        }}>
          Describe your product in plain English. Blitzname generates creative, available names — with trademark screening and brand scoring built in.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 40 }}>
          {FEATURE_PILLS.map(({ color, label }) => (
            <span key={label} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 13px', borderRadius: 20,
              fontSize: 12, fontWeight: 500, color: '#94a3b8',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, boxShadow: `0 0 6px ${color}`, flexShrink: 0 }} />
              {label}
            </span>
          ))}
        </div>
      </section>

      {/* ── Input card ── */}
      <div style={{ position: 'relative', zIndex: 5, maxWidth: 640, margin: '0 auto', padding: '0 40px 80px' }}>

        {/* Textarea */}
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <textarea
            className="landing-textarea"
            value={keywords}
            onChange={e => setKeywords(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe your brand or product — e.g. 'An AI tool that helps developers write better code faster'"
            style={{
              width: '100%', minHeight: 110,
              padding: '18px 20px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 16, color: '#f1f5f9',
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 15, lineHeight: 1.6,
              resize: 'none', outline: 'none',
              backdropFilter: 'blur(8px)',
              boxSizing: 'border-box',
              display: 'block',
              transition: 'border-color 0.2s',
            }}
          />
        </div>

        {/* Example chips */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {EXAMPLE_CHIPS.map(chip => (
            <button key={chip} className="landing-chip" onClick={() => setKeywords(chip)}>
              {chip}
            </button>
          ))}
        </div>

        {/* Config toggle */}
        <button
          className="landing-config-toggle"
          onClick={() => setConfigOpen(o => !o)}
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <circle cx="6.5" cy="6.5" r="2" stroke="currentColor" strokeWidth="1.2" />
            <path d="M6.5 1v1.5M6.5 10.5V12M1 6.5h1.5M10.5 6.5H12M2.6 2.6l1.1 1.1M9.3 9.3l1.1 1.1M9.3 3.7L8.2 4.8M3.7 9.3l-1.1 1.1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          Customize — tone, naming strategy, TLD
          <span style={{
            fontSize: 11,
            display: 'inline-block',
            transition: 'transform 0.2s',
            transform: configOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          }}>›</span>
        </button>

        {/* Config panel */}
        {configOpen && (
          <div style={{
            marginBottom: 24, padding: 20,
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 16,
          }}>
            {/* Tone */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 10 }}>
                Tone
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {TONE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    className="landing-option-pill"
                    onClick={() => setTone(opt.value)}
                    style={{
                      background: tone === opt.value ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.04)',
                      borderColor: tone === opt.value ? 'rgba(99,102,241,0.6)' : 'rgba(255,255,255,0.08)',
                      color: tone === opt.value ? '#a5b4fc' : '#94a3b8',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Naming Strategy */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 10 }}>
                Naming Strategy
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {NAMING_STRATEGY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    className="landing-option-pill"
                    onClick={() => setNamingStrategy(opt.value)}
                    style={{
                      background: namingStrategy === opt.value ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.04)',
                      borderColor: namingStrategy === opt.value ? 'rgba(99,102,241,0.6)' : 'rgba(255,255,255,0.08)',
                      color: namingStrategy === opt.value ? '#a5b4fc' : '#94a3b8',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* TLD */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 10 }}>
                TLD
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {TLD_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    className="landing-option-pill landing-option-pill-tld"
                    onClick={() => setTld(opt)}
                    style={{
                      background: tld === opt ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.04)',
                      borderColor: tld === opt ? 'rgba(99,102,241,0.6)' : 'rgba(255,255,255,0.08)',
                      color: tld === opt ? '#a5b4fc' : '#94a3b8',
                    }}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div style={{ textAlign: 'center' }}>
          <button className="landing-btn-generate" onClick={handleGenerate}>
            <span style={{ fontSize: 20 }}>⚡</span>
            Generate Names
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              padding: '2px 7px', borderRadius: 5,
              background: 'rgba(255,255,255,0.15)',
              fontSize: 11, fontWeight: 600,
              fontFamily: "'JetBrains Mono', monospace",
            }}>⌘↵</span>
          </button>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
            marginTop: 14, fontSize: 12, color: '#475569', fontWeight: 500,
          }}>
            <span>✓ Live domain check</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span>✓ USPTO trademark search</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span>✓ Uses 1 credit</span>
          </div>
        </div>
      </div>

      {/* ── Social proof strip ── */}
      <div style={{
        position: 'relative', zIndex: 5,
        borderTop: '1px solid rgba(255,255,255,0.08)',
        padding: '32px 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 56,
        maxWidth: 640, margin: '0 auto',
        flexWrap: 'wrap',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 24, fontWeight: 800, color: '#f1f5f9',
            letterSpacing: '-0.8px', fontVariantNumeric: 'tabular-nums' as const,
            minWidth: 52,
          }}>
            {counterDisplay}
          </div>
          <div style={{ fontSize: 12, color: '#475569', fontWeight: 500, marginTop: 3 }}>Names generated</div>
        </div>

        <div style={{ width: 1, height: 36, background: 'rgba(255,255,255,0.08)' }} />

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.8px' }}>
            94<span style={{ color: '#6366f1' }}>%</span>
          </div>
          <div style={{ fontSize: 12, color: '#475569', fontWeight: 500, marginTop: 3 }}>Availability accuracy</div>
        </div>

        <div style={{ width: 1, height: 36, background: 'rgba(255,255,255,0.08)' }} />

        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.8px' }}>
            ~3<span style={{ color: '#6366f1' }}>s</span>
          </div>
          <div style={{ fontSize: 12, color: '#475569', fontWeight: 500, marginTop: 3 }}>Average generation time</div>
        </div>
      </div>
    </div>
  );
}
